/**
 * Map ops estimate-from-lead / suggested-price shape into display breakdown.
 * Parallel to mapPrepReviewToBreakdown — ADR-008 live card or ADR-003 engines.
 */

import { classifySuggestionLine } from "./mapEngineSuggestionBreakdown.js";

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function finiteNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Band size + optional vehicle-band customerLabel from the estimate API.
 * @param {string|null|undefined} size
 * @param {string|null|undefined} customerLabel
 * @returns {string|null}
 */
function formatRequiredTruckValue(size, customerLabel) {
  const band = String(size || "").trim();
  if (!band) return null;
  const label = String(customerLabel || "").trim();
  return label ? `${band} · ${label}` : band;
}

/**
 * @param {object|null|undefined} estimatePayload
 *   Full `/pricing/ops/estimate-from-lead` data or nested `estimate` object.
 * @returns {{
 *   source: "ops_estimate",
 *   total: number|null,
 *   context: { key: string, label: string, value: string }[],
 *   lines: { key: string, label: string, amount: number|null, group: string }[],
 *   transportTotal: number|null,
 *   extrasTotal: number|null,
 *   notes: string[],
 *   isPreview: true,
 * }|null}
 */
export function mapOpsEstimateToBreakdown(estimatePayload) {
  if (!estimatePayload || typeof estimatePayload !== "object") return null;

  const estimate =
    estimatePayload.estimate && typeof estimatePayload.estimate === "object"
      ? estimatePayload.estimate
      : estimatePayload;

  const total =
    finiteNumber(estimate.suggestedTotal) ??
    finiteNumber(estimatePayload.breakdown?.total) ??
    null;

  const lineItems = Array.isArray(estimate.lineItems)
    ? estimate.lineItems
    : Array.isArray(estimatePayload.breakdown?.lineItems)
      ? estimatePayload.breakdown.lineItems
      : [];

  const context = [];
  const product =
    estimatePayload.pricingProduct ||
    estimate.pricingProduct ||
    estimate.trace?.pricingProduct;
  if (product) {
    context.push({
      key: "pricingProduct",
      label: "Pricing product",
      value: String(product),
    });
  }

  const architecture =
    estimate.pricingArchitecture ||
    estimate.snapshot?.pricingArchitecture ||
    estimatePayload.pricingArchitecture;
  if (architecture) {
    context.push({
      key: "architecture",
      label: "Architecture",
      value:
        architecture === "adr-008-provider-methodology"
          ? "ADR-008 live card"
          : String(architecture),
    });
  }

  const cardId = estimate.cardId || estimate.snapshot?.cardId;
  if (cardId) {
    context.push({
      key: "liveCard",
      label: "Live card",
      value: String(cardId),
    });
  }

  const pricingMethod = estimate.pricingMethod || estimate.snapshot?.pricingMethod;
  if (pricingMethod) {
    context.push({
      key: "pricingMethod",
      label: "Method",
      value: String(pricingMethod),
    });
  } else if (
    estimate.methodDecisionReason ||
    estimatePayload.methodDecisionReason
  ) {
    context.push({
      key: "pricingMethod",
      label: "Method",
      value: "Unavailable",
    });
  }

  const routeKm =
    finiteNumber(estimate.routeKm) ??
    finiteNumber(estimate.snapshot?.routeKm) ??
    finiteNumber(estimatePayload.routeKm) ??
    finiteNumber(estimatePayload.breakdown?.routeKm);
  const localVolumeMaxKm =
    finiteNumber(estimate.localVolumeMaxKm) ??
    finiteNumber(estimate.snapshot?.localVolumeMaxKm) ??
    finiteNumber(estimatePayload.localVolumeMaxKm) ??
    finiteNumber(estimatePayload.breakdown?.localVolumeMaxKm);
  const methodDecisionReason =
    estimate.methodDecisionReason ||
    estimate.snapshot?.methodDecisionReason ||
    estimatePayload.methodDecisionReason ||
    estimatePayload.breakdown?.methodDecisionReason ||
    null;

  // Always surface local-switch distance decision when any ADR-008 local field is present.
  if (
    routeKm != null ||
    localVolumeMaxKm != null ||
    methodDecisionReason ||
    pricingMethod === "VOLUME" ||
    pricingMethod === "MILEAGE"
  ) {
    context.push({
      key: "routeDistance",
      label: "Route distance",
      value: routeKm != null ? `${routeKm} km` : "Unavailable",
    });
    if (localVolumeMaxKm != null) {
      context.push({
        key: "localVolumeThreshold",
        label: "Local threshold",
        value: `${localVolumeMaxKm} km`,
      });
    }
    if (methodDecisionReason) {
      context.push({
        key: "methodDecisionReason",
        label: "Reason",
        value: String(methodDecisionReason),
      });
    }
  }

  const tariffBasis = estimate.tariffBasis || estimate.snapshot?.tariffBasis;
  if (tariffBasis) {
    context.push({
      key: "tariffBasis",
      label: "Tariff basis",
      value: String(tariffBasis),
    });
  }

  const volumeFt3 = finiteNumber(
    estimate.billableVolumeFt3 ?? estimate.snapshot?.billableVolumeFt3
  );
  if (volumeFt3 != null) {
    context.push({
      key: "volumeFt3",
      label: "Billable volume (ft³)",
      value: `${volumeFt3} ft³`,
    });
  }

  const hundreds = finiteNumber(
    estimate.billableHundredsFt3 ?? estimate.snapshot?.billableHundredsFt3
  );
  if (hundreds != null) {
    context.push({
      key: "hundredsFt3",
      label: "Billable hundreds",
      value: String(hundreds),
    });
  }

  const roundingIncrement = finiteNumber(estimate.roundingIncrement);
  if (roundingIncrement != null && roundingIncrement > 0) {
    context.push({
      key: "rounding",
      label: "Rounding",
      value: `nearest R${roundingIncrement}`,
    });
  }

  const volume =
    finiteNumber(estimate.billableVolumeM3) ??
    finiteNumber(estimatePayload.breakdown?.billableVolumeM3);
  if (volume != null) {
    context.push({
      key: "volume",
      label: "Billable volume",
      value: `${volume} m³`,
    });
  }

  const volumeSource =
    estimatePayload.volumeSource || estimatePayload.breakdown?.volumeSource;
  if (volumeSource) {
    context.push({
      key: "volumeSource",
      label: "Volume source",
      value: String(volumeSource),
    });
  }

  const truckSize =
    estimate.selectedTruckSize ||
    estimate.recommendedTruckSize ||
    estimate.matchedRow?.truckSize ||
    estimate.snapshot?.selectedTruckSize ||
    estimate.snapshot?.recommendedTruckSize ||
    estimatePayload.breakdown?.selectedTruckSize ||
    estimatePayload.breakdown?.recommendedTruckSize ||
    null;
  const truckCustomerLabel =
    estimate.customerLabel ||
    estimate.snapshot?.customerLabel ||
    estimatePayload.breakdown?.customerLabel ||
    null;
  const truckDisplay = formatRequiredTruckValue(truckSize, truckCustomerLabel);
  // Always surface truck fit when the estimate has volume / product context —
  // never invent a band when the API did not return one.
  if (
    truckDisplay ||
    volume != null ||
    estimatePayload.pricingProduct ||
    estimate.pricingProduct
  ) {
    context.push({
      key: "requiredTruck",
      label: "Required truck",
      value: truckDisplay || "Review required",
    });
  }

  const route = estimate.actualRoute;
  if (route?.originProvince || route?.destinationProvince) {
    context.push({
      key: "route",
      label: "Route provinces",
      value: `${route.originProvince || "?"} → ${route.destinationProvince || "?"}`,
    });
  }

  const collectionKm =
    finiteNumber(estimate.originDepotToPickupKm) ??
    finiteNumber(estimatePayload.originDepotToPickupKm) ??
    finiteNumber(estimatePayload.breakdown?.originDepotToPickupKm);
  const deliveryKm =
    finiteNumber(estimate.destDepotToDropKm) ??
    finiteNumber(estimatePayload.destDepotToDropKm) ??
    finiteNumber(estimatePayload.breakdown?.destDepotToDropKm);
  const originHub =
    estimate.originDepotHub ||
    estimatePayload.originDepotHub ||
    estimatePayload.breakdown?.originDepotHub;
  const destHub =
    estimate.destDepotHub ||
    estimatePayload.destDepotHub ||
    estimatePayload.breakdown?.destDepotHub;

  if (collectionKm != null || deliveryKm != null || originHub || destHub) {
    if (collectionKm != null) {
      const hubBit = originHub?.label ? ` · hub ${originHub.label}` : "";
      const src =
        estimate.collectionKmSource ||
        estimatePayload.collectionKmSource ||
        "";
      context.push({
        key: "collectionKm",
        label: "Collection km (one-way)",
        value: `${collectionKm} km${hubBit}${
          src === "hub_haversine" ? " · suggested" : ""
        }`,
      });
    }
    if (deliveryKm != null) {
      const hubBit = destHub?.label ? ` · hub ${destHub.label}` : "";
      const src =
        estimate.deliveryKmSource || estimatePayload.deliveryKmSource || "";
      context.push({
        key: "deliveryKm",
        label: "Delivery km (one-way)",
        value: `${deliveryKm} km${hubBit}${
          src === "hub_haversine" ? " · suggested" : ""
        }`,
      });
    }
  }

  if (estimate.matchedRow?.id) {
    context.push({
      key: "matchedRow",
      label: "Matched rate",
      value: String(estimate.matchedRow.id),
    });
  }

  const lines = lineItems.map((line, index) => {
    const amount = finiteNumber(line?.amount);
    const key = String(line?.ruleKey || line?.key || `line-${index}`);
    const label = String(
      line?.description || line?.label || line?.ruleKey || key
    );
    return {
      key,
      label,
      amount,
      group: classifySuggestionLine(line),
    };
  });

  const sumGroup = (group) => {
    const amounts = lines
      .filter((l) => l.group === group)
      .map((l) => l.amount)
      .filter((n) => n != null);
    if (amounts.length === 0) return null;
    return amounts.reduce((sum, n) => sum + n, 0);
  };

  const notes = [];
  if (estimate.compactSummary) {
    notes.push(String(estimate.compactSummary));
  }

  if (total == null && lines.length === 0 && context.length === 0) {
    return null;
  }

  return {
    source: "ops_estimate",
    total,
    context,
    lines,
    notes,
    transportTotal: sumGroup("transport"),
    extrasTotal: sumGroup("extras"),
    isPreview: true,
  };
}
